/**
 * In-app help data, shared by the staff portal and the customer portal. Pages and What's new come from the
 * client layer, built into each app for that app only (__OPS_HELP__); each login's What's new state and support
 * reports are read under RLS; reports and replies go through the API's /help routes. The staff portal also lists
 * reference documents a director uploads (help_documents, read under RLS, files through /help). In DEV preview
 * mode the database calls are answered from memory.
 */
import { entryKey, helpRouteOf, isGettingStarted, pagesFor, unseenSince, type HelpPage, type WhatsNewEntry } from "@ops/client-config/help";
import { apiRequest, recentApiErrors } from "../lib/api";
import { PREVIEW } from "../lib/preview";
import { supabase } from "../lib/supabase";

export { entryKey };
export type { HelpPage, WhatsNewEntry };

export const HELP = __OPS_HELP__;
export type HelpAppName = typeof HELP.app;

const check = <T>(r: { data: unknown; error: { message: string } | null }): T => {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
};

/* ------------------------------------------------------------------ who is viewing */

/** Set by each app's shell once the login is known: its role decides which pages show. */
export const viewer: { role: string; userId: string | null } = { role: HELP.app === "staff" ? "admin" : "member", userId: null };
export function setHelpViewer(role: string | undefined, userId: string | null) {
  viewer.role = role ?? viewer.role;
  viewer.userId = userId;
}
/** Staff directors and admins see and answer every report. */
export const isManager = () => HELP.app === "staff" && (viewer.role === "director" || viewer.role === "admin");
/** Staff directors add and remove reference documents. */
export const isStaffDirector = () => HELP.app === "staff" && viewer.role === "director";
/** Controllers (crew logins) see crew and all documents; the rest of the office sees them all. */
export const isCrewViewer = () => HELP.app === "staff" && viewer.role === "controller";
export const reportsOn = () => Boolean(HELP.support);

/* ------------------------------------------------------------------ pages */

export const helpPages = (): HelpPage[] => pagesFor(HELP.pages, HELP.app, viewer.role);
export const helpPage = (id: string): HelpPage | undefined => helpPages().find((p) => p.id === id);
export const whatsNew = (): WhatsNewEntry[] => HELP.whatsNew;

/** The screen the user is on, as a help route ("#/leads" for "#/leads/123"; customer "#/milestones" for a milestone). */
export const currentRoute = () => helpRouteOf(window.location.hash, HELP.app);

/**
 * The guide for a screen: one written for it rather than Getting started or Troubleshooting, and one for the
 * viewer's own role before one for everyone (an Accounts login on Overview gets the Accounts page). A page with
 * `guide: no` is never the guide; it shows under Related.
 */
export function pageForRoute(route: string): HelpPage | undefined {
  const pages = helpPages().filter((p) => p.route === route && p.id !== "whats-new" && p.guide !== "no");
  const rank = (p: HelpPage) => (isGettingStarted(p) || /troubleshooting/.test(p.id) ? 3 : p.audience === viewer.role ? 0 : p.audience === "all" ? 1 : 2);
  return [...pages].sort((a, b) => rank(a) - rank(b))[0];
}

/** Other pages for the same screen, and pages this one links to. */
export function relatedPages(page: HelpPage): HelpPage[] {
  const linked = [...page.body.matchAll(/\]\(help:([a-z0-9-]+)\)/g)].map((m) => m[1]);
  return helpPages().filter((p) => p.id !== page.id && p.id !== "whats-new" && (p.route === page.route || linked.includes(p.id)));
}

/** Search: every word must appear in the title, summary or text. */
export function searchPages(q: string): HelpPage[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const pages = helpPages();
  if (!words.length) return pages;
  return pages.filter((p) => {
    const hay = `${p.title}\n${p.summary}\n${p.body}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/* ------------------------------------------------------------------ What's new seen state */

const previewSeen: { key: string | null } = { key: null };

async function myOrg(): Promise<string | null> {
  if (!viewer.userId) return null;
  const table = HELP.app === "staff" ? "profiles" : "customer_users";
  const row = check<{ organisation_id: string } | null>(await supabase.from(table).select("organisation_id").eq("id", viewer.userId).maybeSingle());
  return row?.organisation_id ?? null;
}

/** The What's new entries this login has not seen, newest first. */
export async function unseenWhatsNew(): Promise<WhatsNewEntry[]> {
  const entries = whatsNew();
  if (!entries.length) return [];
  if (PREVIEW) return unseenSince(entries, previewSeen.key);
  if (!viewer.userId) return [];
  const row = check<{ whats_new_seen: string | null } | null>(await supabase.from("help_seen").select("whats_new_seen").eq("user_id", viewer.userId).maybeSingle());
  return unseenSince(entries, row?.whats_new_seen ?? null);
}

export async function markWhatsNewSeen(): Promise<void> {
  const newest = whatsNew()[0];
  if (!newest) return;
  if (PREVIEW) {
    previewSeen.key = entryKey(newest);
    return;
  }
  const org = await myOrg();
  if (!org || !viewer.userId) return;
  check(await supabase.from("help_seen").upsert({ user_id: viewer.userId, organisation_id: org, whats_new_seen: entryKey(newest) }, { onConflict: "user_id" }));
}

/* ------------------------------------------------------------------ support reports */

export type ReportStatus = "open" | "answered" | "fixed";
export interface SupportReport {
  id: string; app: "staff" | "customer"; user_id: string | null; customer_id: string | null; reporter_name: string | null; reporter_email: string | null; role: string;
  route: string | null; platform_version: string | null; user_agent: string | null; local_time: string | null; trying: string; happened: string | null;
  api_errors: { status: number; path: string; message: string }[]; screenshot_key: string | null; status: ReportStatus; reply: string | null;
  replied_at: string | null; replied_by_name: string | null; created_at: string;
}
export const REPORT_STATUS_LABEL: Record<ReportStatus, string> = { open: "Open", answered: "Answered", fixed: "Fixed" };

const previewReports: SupportReport[] = [];
const hoursAgo = (h: number) => new Date(Date.now() - h * 3600_000).toISOString();
if (PREVIEW) {
  const base = { platform_version: "v0.15.0", user_agent: "Preview browser", local_time: null, api_errors: [], screenshot_key: null, reporter_email: "preview@preview.example" };
  previewReports.push(
    { ...base, id: "r-preview-1", app: HELP.app, user_id: "preview", customer_id: HELP.app === "customer" ? "c1" : null, reporter_name: "Preview user", role: viewer.role, route: HELP.app === "customer" ? "#/feedback" : "#/leads", trying: "Attach a second screenshot to my feedback", happened: "The button did nothing.", status: "answered", reply: "Thanks, this is fixed in the next release.", replied_at: hoursAgo(2), replied_by_name: "Sam Rivera", created_at: hoursAgo(26) },
  );
  if (HELP.app === "staff") {
    previewReports.push({ ...base, id: "r-preview-2", app: "customer", user_id: "preview-customer", customer_id: "c1", reporter_name: "Jordan Avery", role: "owner", route: "#/milestones", trying: "Approve the accounts milestone", happened: "It said the milestone had changed.", status: "open", reply: null, replied_at: null, replied_by_name: null, created_at: hoursAgo(3), api_errors: [{ status: 409, path: "/customer/milestones/m3/approve", message: "This milestone has changed. Reload and try again." }] });
  }
}

const REPORT_COLUMNS = "id, app, user_id, customer_id, reporter_name, reporter_email, role, route, platform_version, user_agent, local_time, trying, happened, api_errors, screenshot_key, status, reply, replied_at, replied_by_name, created_at";

/** The signed-in user's own reports, newest first. */
export async function listMyReports(): Promise<SupportReport[]> {
  if (PREVIEW) return previewReports.filter((r) => r.app === HELP.app && r.id === "r-preview-1");
  if (!viewer.userId) return [];
  return check(await supabase.from("support_reports").select(REPORT_COLUMNS).eq("user_id", viewer.userId).order("created_at", { ascending: false }).limit(50));
}

/** Staff directors and admins: every report in the organisation, or one customer's (RLS decides). */
export async function listReports(customerId?: string): Promise<SupportReport[]> {
  if (PREVIEW) return previewReports.filter((r) => !customerId || r.customer_id === customerId);
  let q = supabase.from("support_reports").select(REPORT_COLUMNS).order("created_at", { ascending: false }).limit(200);
  if (customerId) q = q.eq("customer_id", customerId);
  return check(await q);
}

const helpJson = async <T>(path: string, init: RequestInit, timeoutMs = 30_000): Promise<T> => (await (await apiRequest("/help", path, init, { timeoutMs })).json()) as T;

let settingsCache: Promise<{ screenshots: boolean }> | null = null;
/** Whether the report form offers a screenshot: only when the API can keep it (asked once per page load). */
export function reportSettings(): Promise<{ screenshots: boolean }> {
  if (PREVIEW) return Promise.resolve({ screenshots: true });
  settingsCache ??= helpJson<{ screenshots: boolean }>("/settings", { method: "GET" }).catch(() => {
    settingsCache = null;
    return { screenshots: false };
  });
  return settingsCache;
}

export async function sendReport(r: { trying: string; happened: string; route: string; screenshot: Blob | null }): Promise<{ message: string; screenshotKept?: boolean }> {
  if (PREVIEW) {
    previewReports.unshift({ id: `r-${Date.now()}`, app: HELP.app, user_id: "preview", customer_id: null, reporter_name: "Preview user", reporter_email: null, role: viewer.role, route: r.route, platform_version: null, user_agent: navigator.userAgent, local_time: null, trying: r.trying, happened: r.happened || null, api_errors: [], screenshot_key: r.screenshot ? "preview" : null, status: "open", reply: null, replied_at: null, replied_by_name: null, created_at: new Date().toISOString() });
    return { message: `Thanks, ${HELP.support?.team ?? "the team"} has your report.` };
  }
  const f = new FormData();
  f.set("trying", r.trying);
  f.set("happened", r.happened);
  f.set("route", r.route);
  f.set("localTime", new Date().toString().slice(0, 33));
  f.set("apiErrors", JSON.stringify(recentApiErrors().map(({ status, path, message }) => ({ status, path, message }))));
  if (r.screenshot) f.set("screenshot", r.screenshot, r.screenshot instanceof File ? r.screenshot.name : "screenshot.png");
  return helpJson("/reports", { method: "POST", body: f }, 60_000);
}

export async function replyToReport(id: string, status: ReportStatus, reply: string | null): Promise<void> {
  if (PREVIEW) {
    const r = previewReports.find((x) => x.id === id);
    if (r) Object.assign(r, { status, reply, replied_at: new Date().toISOString(), replied_by_name: "Preview user" });
    return;
  }
  await helpJson(`/reports/${id}/reply`, { method: "POST", body: JSON.stringify({ status, reply }) });
}

/** Open a report's screenshot in a new tab (fetched with the access token, so it never has a public link). */
export async function openScreenshot(id: string): Promise<void> {
  if (PREVIEW) return;
  const tab = window.open("", "_blank");
  const blob = await (await apiRequest("/help", `/reports/${id}/screenshot`, { method: "GET" }, {})).blob();
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * A screenshot of the current tab, where the browser can take one (desktop Chrome, Edge and Firefox ask the user
 * to share the tab). Null when it can't or the user says no; on a phone, attach a picture instead.
 */
export async function captureScreen(): Promise<Blob | null> {
  const md = navigator.mediaDevices as (MediaDevices & { getDisplayMedia?: (o: unknown) => Promise<MediaStream> }) | undefined;
  if (!md?.getDisplayMedia) return null;
  let stream: MediaStream | null = null;
  try {
    stream = await md.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false, preferCurrentTab: true, selfBrowserSurface: "include" });
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 250));
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    return await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), "image/png"));
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}
export const canCaptureScreen = () =>
  Boolean((navigator.mediaDevices as (MediaDevices & { getDisplayMedia?: unknown }) | undefined)?.getDisplayMedia) && !/Android|iPhone|iPad/i.test(navigator.userAgent);

/* ------------------------------------------------------------------ reference documents (staff portal) */

// vocab-ok: the field-services document audience (help_documents.audience)
export type DocumentAudience = "office" | "crew" | "all";
export interface HelpDocument { id: string; title: string; audience: DocumentAudience; file_name: string; uploaded_at: string }

export async function listDocuments(): Promise<HelpDocument[]> {
  if (PREVIEW || HELP.app !== "staff") return [];
  return check<HelpDocument[]>(await supabase.from("help_documents").select("id, title, audience, file_name, uploaded_at").is("removed_at", null).order("title"));
}
export const documentFile = async (id: string): Promise<Blob> => (await apiRequest("/help", `/documents/${id}/file`, {}, { timeoutMs: 60_000 })).blob();
export async function uploadDocument(title: string, audience: DocumentAudience, file: File): Promise<void> {
  const f = new FormData();
  f.set("title", title);
  f.set("audience", audience);
  f.set("file", file);
  await apiRequest("/help", "/documents", { method: "POST", body: f }, { timeoutMs: 120_000 });
}
export const removeDocument = async (id: string): Promise<void> => {
  await apiRequest("/help", `/documents/${id}/remove`, { method: "POST" }, {});
};
